import { transformEpicToReplit } from '@/adapters/epic-to-replit-adapter';
import type { ReplitDashboardData } from '@/types/replit-dashboard';
import type { SavedQuestion, CreateQuestionInput, UpdateQuestionInput } from '@/types/saved-question';
import type { MedicalSession } from '@/types/medical-session';
import type { StartupResponse } from '@/types/startup';
import type { DocumentRewriteRequest, DocumentRewriteResponse } from '@/types/document-rewrite';

/**
 * API Client
 * Centralized API client for all backend requests
 */

class ApiClient {
  private baseUrl = '/api';

  /**
   * Fetch Epic FHIR data and transform to Replit format
   */
  async getDashboardData(sessionId: number = 1): Promise<ReplitDashboardData> {
    const response = await fetch(`${this.baseUrl}/patient-data`, {
      credentials: 'include' // Include session cookie
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch patient data: ${response.statusText}`);
    }

    const epicData = await response.json();

    // Transform Epic FHIR to Replit format
    return transformEpicToReplit(epicData, sessionId);
  }

  // =========================================================================
  // Saved Questions API
  // =========================================================================

  async getSavedQuestions(sessionId: string): Promise<SavedQuestion[]> {
    const response = await fetch(`${this.baseUrl}/saved-questions/${sessionId}`, {
      credentials: 'include'
    });

    if (!response.ok) throw new Error('Failed to fetch saved questions');

    const data = await response.json();
    return data.questions;
  }

  async createSavedQuestion(question: CreateQuestionInput): Promise<SavedQuestion> {
    const response = await fetch(`${this.baseUrl}/saved-questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(question)
    });

    if (!response.ok) throw new Error('Failed to create question');

    const data = await response.json();
    return data.question;
  }

  async updateSavedQuestion(id: string, updates: UpdateQuestionInput): Promise<SavedQuestion> {
    const response = await fetch(`${this.baseUrl}/saved-questions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates)
    });

    if (!response.ok) throw new Error('Failed to update question');

    const data = await response.json();
    return data.question;
  }

  async deleteSavedQuestion(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/saved-questions/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) throw new Error('Failed to delete question');
  }

  // =========================================================================
  // Medical Session API
  // =========================================================================

  async getMedicalSession(sessionId: string): Promise<MedicalSession> {
    const response = await fetch(`${this.baseUrl}/medical-session-by-id/${sessionId}`, {
      credentials: 'include'
    });

    if (!response.ok) throw new Error('Failed to fetch medical session');

    const data = await response.json();
    return data.session;
  }

  async updateMedicalSessionNotes(sessionId: string, generalNotes: string): Promise<MedicalSession> {
    const response = await fetch(`${this.baseUrl}/medical-session/${sessionId}/notes`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ generalNotes })
    });

    if (!response.ok) throw new Error('Failed to update session notes');

    const data = await response.json();
    return data.session;
  }

  async updateMedicalSessionSync(sessionId: string): Promise<MedicalSession> {
    const response = await fetch(`${this.baseUrl}/medical-session/${sessionId}/sync`, {
      method: 'PATCH',
      credentials: 'include'
    });

    if (!response.ok) throw new Error('Failed to update session sync time');

    const data = await response.json();
    return data.session;
  }

  // =========================================================================
  // Chat Messages API
  // =========================================================================

  async createNewChat(): Promise<{ conversationId: string }> {
    console.log('[FRONTEND] Creating new chat - calling API...');
    const response = await fetch(`${this.baseUrl}/chat/new`, {
      method: 'POST',
      credentials: 'include'
    });

    console.log('[FRONTEND] New chat response status:', response.status);

    if (!response.ok) {
      const error = await response.text();
      console.error('[FRONTEND] New chat failed:', error);
      throw new Error('Failed to create new chat');
    }

    const data = await response.json();
    console.log('[FRONTEND] New chat created:', data);
    return data;
  }

  async getChatMessages(): Promise<any[]> {
    const response = await fetch(`${this.baseUrl}/chat-messages`, {
      credentials: 'include'
    });

    if (!response.ok) throw new Error('Failed to fetch chat messages');

    const data = await response.json();
    return data.messages;
  }

  async saveChatMessage(message: {
    sessionId: string;
    userId?: number;
    message: string;
    response: string;
    tokensUsed?: number;
    modelUsed?: string;
    responseTimeMs?: number;
  }): Promise<any> {
    const response = await fetch(`${this.baseUrl}/chat-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(message)
    });

    if (!response.ok) throw new Error('Failed to save chat message');

    const data = await response.json();
    return data.message;
  }

  async clearChatHistory(sessionId: string): Promise<{ success: boolean; deletedCount: number }> {
    const response = await fetch(`${this.baseUrl}/chat-messages/${sessionId}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) throw new Error('Failed to clear chat history');

    return response.json();
  }

  async deleteConversation(conversationId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/conversations/${conversationId}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete conversation');
    }
  }

  async batchDeleteConversations(conversationIds: string[]): Promise<{ success: boolean; deletedCount: number }> {
    const response = await fetch(`${this.baseUrl}/conversations/batch-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ conversationIds })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete conversations');
    }

    return response.json();
  }

  // =========================================================================
  // Lab Classification API
  // =========================================================================

  async classifyLabs(params: {
    labs: Array<{
      id: string;
      testName: string;
      result: string | number;
      unit: string;
      referenceRange?: string | null;
    }>;
    patientAge: string;
    patientGender: string;
  }): Promise<{
    classifications: Array<{
      id: string;
      status: 'normal' | 'low' | 'high' | 'critical';
      explanation?: string;
    }>;
  }> {
    const response = await fetch(`${this.baseUrl}/labs/classify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(params)
    });

    if (!response.ok) throw new Error('Failed to classify labs');

    return response.json();
  }

  // =========================================================================
  // Startup Generation API
  // =========================================================================

  /**
   * One model call at login: summary, what's-new bullets, section overviews,
   * and lab statuses. Rejects on any non-2xx (409 when the admin toggle is
   * off, 502 when the model output did not parse, 500 otherwise) so the
   * Dashboard can fall back to the separate current-status call.
   */
  async generateStartup(
    dashboardData: ReplitDashboardData,
    language: 'en' | 'es'
  ): Promise<StartupResponse> {
    const response = await fetch(`${this.baseUrl}/generate-startup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ patientData: dashboardData, language })
    });

    if (!response.ok) throw new Error(`Failed to generate startup: ${response.status}`);

    return response.json();
  }

  // =========================================================================
  // Document Rewrite API
  // =========================================================================

  /**
   * Plain-language rewrite of one note or imaging report the client already
   * holds. Rejects on any non-2xx (413 too long, 502 unparseable, 500
   * otherwise) so the section falls back to the original text.
   */
  async rewriteDocument(params: DocumentRewriteRequest): Promise<DocumentRewriteResponse> {
    const response = await fetch(`${this.baseUrl}/rewrite-document`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(params)
    });

    if (!response.ok) throw new Error(`Failed to rewrite document: ${response.status}`);

    return response.json();
  }
}

// Export singleton instance
export const api = new ApiClient();
